<script setup lang="ts">
import { isNull } from 'lodash'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useLazyQuery, useSubscription } from '@vue/apollo-composable'
import { CombinedGraphQLErrors } from '@apollo/client/core'
import AppHeader from '@/components/layouts/header/AppHeader.vue'
import AppFooter from '@/components/layouts/AppFooter.vue'
import SignInView from '@/components/views/SignInView.vue'
import { useUserStore } from '@/stores/user.store.ts'
import { HEADER_PADDING_TOP_PX } from '@/config/visual.const.ts'
import { DEFAULT_ENVIRONMENT, getDefaultClientEnv } from '@/config/env.const.ts'
import router, { buildPathByErrorName } from '@/router.ts'
import {
  BackendErrorCode,
  ChatEvent,
  Environment,
  ErrorName,
  ProvideElement,
  WebsocketConnectionState
} from '@/enums.ts'
import { FindProjectsCompactQuery } from '@/components/projects/graphql/project.queries.ts'
import { GetCurrentUserInfoAndProjects } from '@/components/users/graphql/user.queries.ts'
import { WSLinkStore } from '@/graphql.ts'
import { useUiStore } from '@/stores/ui.store.ts'
import type { Project } from '@/components/projects/interfaces.ts'
import type { AuthorizeUserResponse } from '@/components/users/interfaces.ts'
import { useTranslationStore } from '@/stores/translation.store.ts'
import { GET_CHAT_MESSAGES_SUBSCRIPTION } from '@/components/chat/graphql/subscriptions.graphql.ts'
import type { ChatWSMessage } from '@/components/chat/interfaces/chat.interfaces.ts'
import { useToast } from 'vue-toastification'
import ChatNewMessageReceivedToast from '@/components/toasts/ChatNewMessageReceivedToast.vue'
import { ChatBubbleBottomCenterIcon } from '@heroicons/vue/20/solid'
import { useTranslations } from '@/composables/useTranslations.ts'

const { t } = useTranslations()
const toast = useToast()
const route = useRoute()
const uiStore = useUiStore()
const userStore = useUserStore()
const { getCurrentProjectKey, getProjects } = useUserStore()
const { isAuthorized, wsConnectionState } = storeToRefs(userStore)
const { isLoaded: isTranslationsLoaded } = storeToRefs(useTranslationStore())
const {
  onResult: onCurrentUserInfoAndProjectsLoaded,
  onError: onCurrentUserInfoAndProjectsLoadingFailed,
  load: loadCurrentUserInfoAndProjects
} = useLazyQuery<{ user: AuthorizeUserResponse, projects: Project[] }>(GetCurrentUserInfoAndProjects)
const {
  onResult: onUserProjectsLoaded,
  onError: onUserProjectsLoadingFailed,
  load: loadUserProjects
} = useLazyQuery<{ projects: Project[] }>(FindProjectsCompactQuery)
let isConnectedFirstTime = true
const isConnectingBannerVisible = computed<boolean>(() => {
  return wsConnectionState.value !== WebsocketConnectionState.Connected && !isConnectedFirstTime
})

onCurrentUserInfoAndProjectsLoaded(({ user, projects }) => {
  userStore.authorize(user)
  userStore.setProjectKeyFromRouteParams(route.params)
  userStore.setProjectsAndSelectIfProjectKeyProvidedInUrl(projects, onProjectSelectionFailed)
})

onCurrentUserInfoAndProjectsLoadingFailed((err) => {
  if (CombinedGraphQLErrors.is(err) && err.errors.map((error) => error.message).includes(BackendErrorCode.Unauthorized)) {
    userStore.unAuthorize()
  }
})

onUserProjectsLoaded(({ projects }) => {
  userStore.setProjectsAndSelectIfProjectKeyProvidedInUrl(projects, onProjectSelectionFailed)
})

onUserProjectsLoadingFailed((err) => {
  console.error('onUserProjectsLoadingFailed | Error:', err)
})

function onProjectSelectionFailed (): void {
  router.push({
    path: buildPathByErrorName(ErrorName.ProjectNotFound)
  })
}

function onScroll (): void {
  uiStore.setStickyHeader(window.scrollY > HEADER_PADDING_TOP_PX)
}

function onLoginSucceed (user: AuthorizeUserResponse) {
  userStore.authorize(user)
  loadUserProjects()
  initializeGraphQLSubscriptions()
}

function initializeGraphQLSubscriptions (): void {
  WSLinkStore[getDefaultClientEnv()].client.on('closed' , (event) => {
    DEFAULT_ENVIRONMENT !== Environment.Production && console.log('GraphQL WS client | Closed | Event:', event)
    userStore.setWebsocketConnectionState(WebsocketConnectionState.Disconnected)
  })
  WSLinkStore[getDefaultClientEnv()].client.on('connected' , (event) => {
    DEFAULT_ENVIRONMENT !== Environment.Production && console.log('GraphQL WS client | Connected | Event:', event)
    userStore.setWebsocketConnectionState(WebsocketConnectionState.Connected)
    isConnectedFirstTime = false
  })
  WSLinkStore[getDefaultClientEnv()].client.on('connecting' , (event) => {
    DEFAULT_ENVIRONMENT !== Environment.Production && console.log('GraphQL WS client | Connecting | Event:', event)
    userStore.setWebsocketConnectionState(WebsocketConnectionState.Connecting)
  })
  WSLinkStore[getDefaultClientEnv()].client.on('error' , (err) => {
    DEFAULT_ENVIRONMENT !== Environment.Production && console.log('GraphQL WS client | Error:', err)
  })
}

function getSenderNameByEvent (from: ChatEvent): string {
  switch (from) {
    case ChatEvent.Customer:
      return t('chats.page.customer')
    case ChatEvent.AI:
      return t('chats.page.ai')
    case ChatEvent.CustomerSupport:
      return t('chats.page.customerSupport')
    default:
      return 'System'
  }
}

onMounted(async () => {
  await router.isReady()
  window.addEventListener('scroll', onScroll)

  try {
    await loadCurrentUserInfoAndProjects()
    initializeGraphQLSubscriptions()
  } catch (err) {
    console.log('App.vue | onMounted | Err:', err)
  }
})

onUnmounted(() => {
  console.debug('App.vue | onUnmounted')
  window.removeEventListener('scroll', onScroll)
})

const {
  result: newMessages,
  onError: onNewMessagesSubscriptionFailed
} = useSubscription<{ chatMessages: ChatWSMessage[] }>(GET_CHAT_MESSAGES_SUBSCRIPTION, { fetchPolicy: 'no-cache' })

watch(newMessages, (newMessagesValue) => {
  if (!newMessagesValue) {
    return
  }

  for (const message of newMessagesValue.chatMessages) {
    const project = getProjects().find(({ id }) => id === message.projectId)
    const projectTitle = project?.title ?? 'N/A'
    const projectKey = project?.key ?? '___'


    if (!route.path.includes('/chats/')) {
      toast.success({
        component: ChatNewMessageReceivedToast,
        props: {
          title: `${projectTitle} | #${message.chatIndex}`,
          message: `${getSenderNameByEvent(message.event)}: ${message.content}`,
        }
      }, {
        icon: ChatBubbleBottomCenterIcon,
        onClick: (closeToast) => {
          router.push({
            path: `/${projectKey}/chats/${message.chatIndex}`
          })
          closeToast()
        }
      })
    }
  }
})

onNewMessagesSubscriptionFailed((err) => {
  console.log('onNewMessagesSubscriptionFailed | err:', err)
})
</script>

<template>
  <!--
  <div v-if="isConnectingBannerVisible" class="text-white bg-red-500 px-6 py-2.5 sm:px-3.5 sm:before:flex-1 w-full fixed z-[1000] text-center">
    Connecting...
  </div>-->
  <div
    v-if="DEFAULT_ENVIRONMENT !== Environment.Production"
    class="theenv fixed bg-indigo-500 text-yellow-300 text-center z-50 font-semibold h-9 font text-xl"
  >
    {{ DEFAULT_ENVIRONMENT.toUpperCase() }}
  </div>
  <div v-if="isNull(isAuthorized)"></div>
  <div v-else-if="isAuthorized && isTranslationsLoaded" class="min-h-screen flex flex-col bg-[linear-gradient(98.89deg,#D9E7FB_0%,#FFEBB9_96.04%)]">
    <AppHeader/>
    <div class="grow">
      <RouterView />
    </div>
    <AppFooter/>
  </div>
  <div v-else>
    <SignInView @onLoginSucceed="onLoginSucceed" v-if="isTranslationsLoaded"/>
  </div>
</template>
<style scoped>

.theenv {
  left: -53px;
  top: 29px;
  width: 200px;
  transform: rotate(315deg);
  line-height: 35px;
}
</style>
